import { AxiosResponse } from 'axios';
import { LdJsonBreadCrumbsResort, LdJsonSkiResort } from 'components/LdJson';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React, { useCallback, useState } from 'react';
import { Routes } from 'routes';
import { getResortSlug } from 'util/memcache';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortRelated,
  fetchResortShortInfo, fetchResortShortWeatherReport,
  fetchResortSkiRentals,
  fetchResortSnowReport,
} from 'api/resort/static';
import {
  IResortPageInfo, IResortPageSnowInfo, IResortPathInfo, ISkiResortShop,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';

import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import {
  NotFoundError, assertShouldBeNot404, wrapServerSideProps, wrapStaticProps,
} from 'util/wrapProps';
import styles from 'page-components/resort/lodging.module.scss';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { ITASkiRentalReq } from 'components/common/PlanYourTrip/types';
import { SkiRentalShop } from 'components/resort/SkiRentalShop';
import { PlaningViewsEnum } from 'components/common/PlanYourTrip/constants';
import getSkiRentalUrl from 'components/resort/SkiRentalShop/getSkiRentalUrl';

export type ResortOverviewPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  shops: Array<ISkiResortShop>,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-skirentals-page';
export const TEMPLATE_ID = '3.2.7';
export const TEMPLATE_NAME = 'Resort Ski Rental';

export default function ResortOverviewPage({
  fullResort,
  shops,
  pathInfo,
}: ResortOverviewPageProps) {
  const { t } = useOTSTranslation('common');
  const [formData, setFormData] = useState<ITASkiRentalReq>({
    checkIn: '',
    checkOut: '',
    numChildren: 0,
    numAdults: 1,
  });

  const baseSkiShop = (shops ?? []).find((sh) => `${sh.resortId}` === pathInfo.uuid);
  const anySkiShop = (shops ?? []).length ? shops[0] : undefined;
  const baseSkiUrl = (baseSkiShop || anySkiShop)?.url;

  const handleChangeAccommodationParams = useCallback(
    (params: ITASkiRentalReq, submitted: boolean) => {
      setFormData(params);
      if (submitted && baseSkiUrl) {
        const finalUrl = getSkiRentalUrl(baseSkiUrl, params);
        window.open(finalUrl, '_blank');
      }
    },
    [setFormData],
  );

  if (!fullResort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <>
        <div className="w-100">
          <PlanYourTrip
            initialTab={PlaningViewsEnum.rental}
            resort={pathInfo}
            hasSkiRental
            hasCarRental={pathInfo.hasCarRental}
            hasFlights={pathInfo.hasFlights}
            onSkiRentalChange={handleChangeAccommodationParams}
            rentalUrl={fullResort.rentalUrl}
            liftsUrl={fullResort.liftsUrl}
            lessonsUrl={fullResort.lessonsUrl}
            isPartner={fullResort.isPartner}
            resortCountry={fullResort?.region?.country_code}
          />
          {!(shops?.length) && (
            <h2 className="m-4 p-4" style={{ textAlign: 'center' }}>
              <span>{t('resort.skiRentals.noResults' as any)}</span>
            </h2>
          )}
          {!!(shops?.length) && (
            <div className={styles.offerList}>
              {shops.map((shop) => <SkiRentalShop shop={shop} key={shop.id || shop.uuid} query={formData} />)}
            </div>
          )}
        </div>
      </>
      <LdJsonSkiResort
        resort={fullResort}
      />
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={fullResort.region}
        fn={Routes.resortSkiRentals}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortOverviewPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid, hasSkiRental, title } = cache.data;

  if (!hasSkiRental) {
    throw new NotFoundError(`Ski rental do not exist for ${title}.`);
  }

  const res = await fetchResortShortInfo(uuid);
  const fullResort = await fetchResortSnowReport(uuid);

  return {
    type: 'resort',
    route: 'skirentals',
    resort: res,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    fullResort,
    shops: await fetchResortSkiRentals(uuid),
    pathInfo: cache.data,

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.SKI_RENTALS,
      tab: CurrentTab.TRIP_PLANNING,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort profile',
    hero: {
      title: 'resort.skiRental.heroTitle',
      showVideo: true,
    },
    meta: {
      description: 'Resort.skirentals.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.skirentals.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
  };
};

// This function gets called at build time
export const getResortOverviewPageStaticProps = wrapStaticProps(fetchProps);

export const getResortOverviewPageServerSideProps = wrapServerSideProps(fetchProps);
